import React, { useEffect, useState } from "react";
import { IntlShape } from "react-intl";
import { FieldAttributes } from "formik";
import { match as Match } from "react-router-dom";
import { toast } from "react-toastify";
import Block from "../../../../../components/Tailwind/Block";
import Header from "../../../../../components/Tailwind/Block/Header";
import DefaultForm, {
  AdditionalFieldAttributes,
} from "../../../../../components/Form";
import overrideClasses from "../../../../../utils/overrideClasses";
import { GroundGraphqlContextStore } from "../../../../../lib/ground-aws-graphql-core";
import { isString } from "../../../../../utils/types";
import history from "../../../../../history";
import { EnumPaths } from "../../../../../utils/navigation";

interface Props {
  match?: Match<{ id: string }>;
  intl: IntlShape;
  edition: boolean;
}

const QuoteRequestStatusForm = (props: Props): JSX.Element => {
  const { intl, match, edition } = props;
  const [loading, setLoading] = useState<boolean>(false);
  const [togglePanel, setTogglePanel] = useState(false);

  const getQuoteRequestStatus = GroundGraphqlContextStore.useStoreActions(
    actions => actions.quoteRequests.getQuoteRequestStatus
  );

  const createQuoteRequestStatus = GroundGraphqlContextStore.useStoreActions(
    actions => actions.quoteRequests.createQuoteRequestStatus
  );

  const updateQuoteRequestStatus = GroundGraphqlContextStore.useStoreActions(
    actions => actions.quoteRequests.updateQuoteRequestStatus
  );

  const setQuoteRequestStatus = GroundGraphqlContextStore.useStoreActions(
    actions => actions.quoteRequests.setQuoteRequestStatus
  );

  const quoteRequestStatus = GroundGraphqlContextStore.useStoreState(
    state => state.quoteRequests.quoteRequestStatus
  );

  const formFields: FieldAttributes<AdditionalFieldAttributes>[] = [
    {
      name: "code",
      type: "number",
      label: "page.quote.request.status.code",
      placeholder: "page.quote.request.status.code.placeholder",
      initialValue: quoteRequestStatus?.code,
      required: true,
      disabled: edition,
      min: 0,
      validate: value => {
        if (value >= 0) return "";

        return "page.quote.request.status.code.more.than.zero";
      },
    },
    {
      name: "label",
      label: "page.quote.request.status.label",
      placeholder: "page.quote.request.status.label.placeholder",
      initialValue: quoteRequestStatus?.label,
      required: true,
      translatable: true,
    },
  ];

  const fetchData = () => {
    setLoading(true);
    if (match?.params.id) {
      getQuoteRequestStatus({ id: match?.params.id }).finally(() => {
        setLoading(false);
      });
    } else {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();

    // Clean the event from the state when we unmount this component
    return () => {
      setQuoteRequestStatus(null);
    };
  }, []);

  const handleSubmit = (values: any) => {
    setLoading(true);
    const { label, ...others } = values;
    if (edition && quoteRequestStatus) {
      updateQuoteRequestStatus({
        ...others,
        label: isString(label) ? JSON.parse(label) : label,
        id: quoteRequestStatus.id,
      })
        .then(() => {
          history.push(`/${EnumPaths.ROOT}/${EnumPaths.QUOTE_REQUEST_STATUS}`);
          toast(
            intl.formatMessage({
              id: "page.quote.request.status.update.success",
            }),
            { type: "success" }
          );
        })
        .catch(() => {
          history.push(`/${EnumPaths.ROOT}/${EnumPaths.QUOTE_REQUEST_STATUS}`);
          toast(
            intl.formatMessage({
              id: "page.quote.request.status.update.error",
            }),
            {
              type: "error",
            }
          );
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      createQuoteRequestStatus({
        ...others,
        label: isString(label) ? JSON.parse(label) : label,
      })
        .then(() => {
          history.push(`/${EnumPaths.ROOT}/${EnumPaths.QUOTE_REQUEST_STATUS}`);
          toast(
            intl.formatMessage({
              id: "page.quote.request.status.create.success",
            }),
            { type: "success" }
          );
        })
        .catch(() => {
          history.push(`/${EnumPaths.ROOT}/${EnumPaths.QUOTE_REQUEST_STATUS}`);
          toast(
            intl.formatMessage({
              id: "page.quote.request.status.create.error",
            }),
            { type: "error" }
          );
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  return loading ? (
    <div className="loading" />
  ) : (
    <>
      {/**
       * When we were loading the form was initialized again and we would lose all data
       * Show loading here in order to not lose the fields data after a loading
       */}
      <div className={overrideClasses({ loading })} />

      {/* Hide when loading */}
      <div className={overrideClasses("px-8 pb-32", { hidden: loading })}>
        <Block>
          <Header
            item={quoteRequestStatus}
            title={
              edition
                ? "page.list.quote.request.status.update.quote.request.status"
                : "page.list.quote.request.status.create.quote.request.status"
            }
            className="border-b border-gray-200"
          />

          <DefaultForm
            fields={formFields}
            onSubmit={handleSubmit}
            showPanel={togglePanel}
          />
        </Block>
      </div>
    </>
  );
};

export default QuoteRequestStatusForm;
